import { Box, Button, SxProps, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import TrustedSupplier from '../assets/images/trustedSupplier.png';
import TrustedSupplierIcon from '../assets/icons/trusted_icon.svg?react';
import { ContractsAsBuyerDocument } from '../queries/__generated__/contractsAsBuyer.generated';
import { ContractsAsSupplierDocument } from '../queries/__generated__/contractsAsSupplier.generated';
import { commonSnackbarPropsAtom } from '../common/state/state';
import { ContractPreview } from '../hooks/useContracts';
import { useUpdateContractByBuyerMutation } from '../mutations/__generated__/updateContractByBuyer.generated';
import { currentContextAtom } from '../state';
import TrustedPartnerDialog from './TrustedPartnerDialog';

interface TrustedSupplierCardProps {
  contract: ContractPreview;
  sx?: SxProps;
  onClose?: () => void;
  onAction?: (activate: boolean) => void;
  imageSx?: SxProps;
}

export default function TrustedSupplierCard({ contract, sx, imageSx, onClose, onAction }: TrustedSupplierCardProps): React.JSX.Element {
  const { t } = useTranslation(['contracts', 'common']);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const { companyId } = useAtomValue(currentContextAtom);
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);
  const [updateContract] = useUpdateContractByBuyerMutation({
    refetchQueries: [ContractsAsBuyerDocument, ContractsAsSupplierDocument],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      if (data.updateContract?.contractId && !onAction) {
        setCommonSnackbarProps({
          label: t('contracts:trusted_supplier:trusted_partnership_activated'),
          snackbarProps: { open: true },
        });
      }
    },
  });

  const handleOpenDialog = (): void => {
    setDialogOpen(true);
  };

  const handleCloseDialog = (activated?: boolean): void => {
    if (onClose) {
      onClose();
    }
    setDialogOpen(false);
    if (activated) {
      updateContract({
        variables: {
          data: {
            contractId: contract.id,
            companyId,
            contractInput: {
              isTrustedSupplier: true,
            },
          },
        },
      });
    }

    if (onAction) {
      onAction(!!activated);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: 'primaryContainer.main',
        borderRadius: 4,
        width: { lg: 640 },
        pl: { xs: 1, md: 2 },
        pr: 1,
        py: 1,
        display: 'flex',
        flexDirection: {
          xs: 'column-reverse',
          md: 'row',
        },
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        ...sx,
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', mb: 0.5, gap: 0.5 }}>
          <TrustedSupplierIcon width={24} height={24} style={{ flexShrink: 0 }} />
          <Typography
            variant="titleMedium"
            sx={{
              color: 'onPrimaryContainer.main',
            }}
          >
            {t('contracts:trusted_supplier.title')}
          </Typography>
        </Box>
        <Typography
          variant="bodySmall"
          sx={{
            color: 'onPrimaryContainer.main',
            mb: 1.5
          }}
        >
          {t('contracts:trusted_supplier.description', { companyName: contract.company.tradeName })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
          <Button variant="outlined" color="primary" onClick={handleOpenDialog}>
            {t('contracts:trusted_supplier.learn_more')}
          </Button>
          {!onAction && (
            <Button variant="contained" color="primary" onClick={handleOpenDialog}>
              {t('contracts:trusted_supplier.activate_partnership')}
            </Button>
          )}
        </Box>
      </Box>
      <Box
        component="img"
        src={TrustedSupplier}
        sx={{
          width: {
            xs: '100%',
            md: 200,
          },
          aspectRatio: { xs: '3/1', md: '4/3' },
          objectFit: 'cover',
          borderRadius: 2,
          ...imageSx,
        }}
      />
      <TrustedPartnerDialog open={isDialogOpen} onClose={handleCloseDialog} contract={contract} />
    </Box>
  );
}
